import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import VehicleConditionMap from './VehicleConditionMap';
import ConditionDetailsModal from './ConditionDetailsModal';
import ConditionInspectionList from './ConditionInspectionList';
import { updateDamage, deleteDamage } from '../AddVehicleActions';
import { CommonSweetAlertVariants } from '../../constants/CommonSweetAlertVariants';
import CommonSweetAlert from '../../common/components/CommonSweetAlert';
import { DamageTypes } from '../../constants/DamageTypes';
import closeIcon from '../../assets/img/icons/close.svg';
import '../../assets/css/componentStyles/ConditionInspection.css';

function ConditionInspectionForm({ form }) {
  const dispatch = useDispatch();
  const damages = useSelector((state) => state.addVehicle.getIn(['damages']));

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeDamage, setActiveDamage] = useState(null);
  const [damageToDelete, setDamageToDelete] = useState(null);

  const damagesArray = useMemo(() => {
    if (!damages) return [];
    return damages.toJS ? damages.toJS() : damages;
  }, [damages]);

  const getDamageTypeLabel = useCallback((damage) => {
    const exactMatch = DamageTypes.find(
      (type) =>
        type.value === damage.damageType ||
        type.value === damage.type ||
        type.value === damage.damageType?.value,
    );
    if (exactMatch) return exactMatch.label;

    return damage.label || damage.damageType?.label || 'damage';
  }, []);

  const toggleModal = useCallback(() => setIsModalOpen(!isModalOpen), [isModalOpen]);

  const handleMapClick = useCallback((relativeX, relativeY, section) => {
    if (relativeX === 0 && relativeY === 0) {
      return;
    }

    const backendSectionName = section === 'bottom' ? 'EXTERNAL' : 'INTERNAL';

    setActiveDamage({
      coordinateX: relativeX,
      coordinateY: relativeY,
      section: backendSectionName,
    });
    setIsModalOpen(true);
  }, []);

  const handleSaveDamage = useCallback(
    (damageData) => {
      // If editing (has id), set isEdit: true, else do not set isEdit
      const damageToSave = damageData.id
        ? { ...damageData, isEdit: true }
        : { ...damageData, id: uuidv4() };

      if (form) {
        const currentDamages = form.values.vehicleDamages?.damages || [];
        const existingIndex = currentDamages.findIndex((d) => d.id === damageToSave.id);
        const updatedDamages =
          existingIndex >= 0
            ? currentDamages.map((d, i) => (i === existingIndex ? damageToSave : d))
            : [...currentDamages, damageToSave];

        form.setFieldValue('vehicleDamages.damages', updatedDamages);
        dispatch(updateDamage(damageToSave));
      } else {
        dispatch(updateDamage(damageToSave));
      }

      setActiveDamage(null);
    },
    [dispatch, form],
  );

  const handleEditDamage = useCallback(
    (damageId) => {
      const damageToEdit = damagesArray.find((d) => d.id === damageId);
      if (damageToEdit) {
        setActiveDamage(damageToEdit);
        setIsModalOpen(true);
      }
    },
    [damagesArray],
  );

  const handleDeleteDamage = useCallback(
    (damageId) => {
      const damageToRemove = damagesArray.find((d) => d.id === damageId);
      setDamageToDelete(damageToRemove);
    },
    [damagesArray],
  );

  const confirmDeleteDamage = useCallback(() => {
    if (damageToDelete) {
      if (form) {
        const currentDamages = form.values.vehicleDamages?.damages || [];
        const updatedDamages = currentDamages.filter((d) => d.id !== damageToDelete.id);
        form.setFieldValue('vehicleDamages.damages', updatedDamages);
        dispatch(deleteDamage(damageToDelete.id));
      } else {
        dispatch(deleteDamage(damageToDelete.id));
      }
      setDamageToDelete(null);
    }
  }, [damageToDelete, dispatch, form]);

  const cancelDeleteDamage = useCallback(() => {
    setDamageToDelete(null);
  }, []);

  const headerContent = useMemo(
    () => ({
      title: 'Condition Inspection',
      description:
        'Click on the graphic below in the area where a damage exists and fill up all necessary details.',
    }),
    [],
  );
  const damageModalIndex = useMemo(() => {
    if (activeDamage?.id) {
      const existingIndex = damagesArray.findIndex((d) => d.id === activeDamage.id);
      return existingIndex;
    }
    return damagesArray.length;
  }, [activeDamage, damagesArray]);
  return (
    <AddVehicleContentWrapper>
      <SettingsSectionHeader
        header={headerContent.title}
        description={headerContent.description}
      />
      <VehicleConditionMap
        damages={damagesArray}
        onMapClick={handleMapClick}
        onMarkerClick={handleEditDamage}
      />
      <ConditionInspectionList
        damages={damagesArray}
        onEdit={handleEditDamage}
        onDelete={handleDeleteDamage}
      />
      <ConditionDetailsModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        onSave={handleSaveDamage}
        damage={activeDamage}
        damagesIndex={damageModalIndex}
      />
      <CommonSweetAlert
        show={!!damageToDelete}
        variant={CommonSweetAlertVariants.PRIMARY_RED}
        onConfirm={confirmDeleteDamage}
        onCancel={cancelDeleteDamage}
        confirmButtonText="DELETE"
        className="condition-inspection__sweet-alert"
        noIcon
      >
        <button
          type="button"
          className="condition-inspection__sweet-alert-close"
          aria-label="Close"
          onClick={cancelDeleteDamage}
        >
          <img src={closeIcon} alt="Close" width={30} height={30} />
        </button>
        <SettingsSectionHeader
          header="Delete Damage Item"
          headerClassName="condition-inspection__sweet-alert-header"
        />
        <p className="common-sweet-alert__title">
          Please confirm that you want to delete this{' '}
          <b>{damageToDelete ? getDamageTypeLabel(damageToDelete) : 'damage'}</b> item
        </p>
      </CommonSweetAlert>
    </AddVehicleContentWrapper>
  );
}

ConditionInspectionForm.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
};

ConditionInspectionForm.defaultProps = {
  form: null,
};

export default ConditionInspectionForm;
